import React, { useEffect, useState } from "react";
import {
  getCommunityPathwayData,
  communityAPI,
  communityNodeAPI,
} from "api/Community/CommunityAPI";
import { Community } from "api/Community/CommunityInterfaces";
import CommunityFlow from "./CommunityDetail/CommunityFlow";
import CommunityEmptyPage from "./CommunityEmptyPage";
import GenericRecordSummary from "components/Records/RecordDisplay/GenericRecordSummary";

interface CommunityDetailProps {
  community: Community | null;
  selectedNode: any | null;
  onNodeSelect: (node: any | null) => void;
  onUpdateNode: (node: any) => void;
  savingNode: boolean;
  onCommunityCreated?: (newCommunity: Community) => void;
}

const CommunityDetail: React.FC<CommunityDetailProps> = ({
  community: communityProp,
  selectedNode,
  onNodeSelect,
  onUpdateNode,
  savingNode,
  onCommunityCreated,
}) => {
  const [community, setCommunity] = useState<Community | null>(communityProp);
  const [nodes, setNodes] = useState<any[]>([]);
  const [edges, setEdges] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Keep local community in sync if the prop changes externally
  useEffect(() => {
    setCommunity(communityProp);
  }, [communityProp]);
  
  // Fetch nodes + edges when community changes
  useEffect(() => {
    if (!community) return;
    setLoading(true);
    getCommunityPathwayData(String(community.id))
      .then((data) => {
        setNodes(data.nodes || []);
        setEdges(data.edges || []);
      })
      .finally(() => setLoading(false));
  }, [community]);
  
  // Generic field update for GenericRecordSummary
  const handleCommunityUpdate = async (id: number, field: string, value: any) => {
    if (!community) return;
    setSaving(true);
    try {
      await communityAPI.updateField(id, field, value);
      setCommunity((prev) => (prev ? { ...prev, [field]: value } : prev));
    } catch (error) {
      console.error(`Failed to update community ${field}:`, error);
    } finally {
      setSaving(false);
    }
  };

  const handleNodeUpdate = async (updatedNode: any) => {
    if (!community || !updatedNode?.id) return;

    try {
      // Call the new API for a single node update
      const savedNode = await communityNodeAPI.update(Number(updatedNode.id), updatedNode);

      // Update local state immediately with the saved node
      setNodes((prev) =>
        prev.map((n) =>
          n.id === updatedNode.id ? { ...n, ...savedNode } : n
        )
      );

      onNodeSelect(savedNode);
      onUpdateNode(savedNode);
    } catch (error) {
      console.error("Failed to update node:", error);
    }
  };

  
  return (
    <div>
      {community && (
        <GenericRecordSummary
          record={community}
          titleField="name"
          descriptionField="description"
          onUpdate={handleCommunityUpdate}
          loading={saving}
          onClose={() => setCommunity(null)}
        />
      )}

      <div style={{ padding: "20px" }}>
        {loading ? (
          <p>Loading community flow...</p>
        ) : community ? (
          <CommunityFlow
            nodes={nodes}
            edges={edges}
            setNodes={setNodes}
            setEdges={setEdges}
            onNodeSelect={onNodeSelect}
            CommunityId={String(community.id)}
            onNodesReload={async () => {
              const data = await getCommunityPathwayData(String(community.id));
              setNodes(data.nodes || []);
              setEdges(data.edges || []);
            }}
          />
        ) : (
          <CommunityEmptyPage
            onCommunityCreated={(newCommunity) => {
              onCommunityCreated?.(newCommunity);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default CommunityDetail;
